import TweenOne from 'rc-tween-one'
import { Transition } from 'react-transition-group' // ES6
import * as d3 from 'd3'
import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import {
  useDebounce,
  useIntersectionObserver,
  useWindowSize,
} from 'usehooks-ts'

const COLORS: { [key: string]: string } = {
  'gender.1.m': 'var(--blue-bright-1)',
  'gender.2.f': 'var(--red-bright-1)',
  'gender.3.d': 'var(--gray-3)',
  'levels.2.kanton': 'blue',
  'levels.1.bund': 'red',
  'levels.3.gemeinde': 'green',
  'levels.3.gemeindeklein': 'yellow',
  participated: 'var(--petrol-bright-1)',
}
const BRIGHTCOLORS: { [key: string]: string } = {
  'gender.1.m': 'var(--blue-bright-3)',
  'gender.2.f': 'var(--red-bright-3)',
  'gender.3.d': 'var(--gray-4)',
  'levels.2.kanton': 'blue',
  'levels.1.bund': 'red',
  'levels.3.gemeinde': 'green',
  'levels.3.gemeindeklein': 'yellow',
  participated: 'var(--petrol-bright-3)',
}
const DARKCOLORS: { [key: string]: string } = {
  'gender.1.m': 'var(--blue-dark-2)',
  'gender.2.f': 'var(--red-dark-2)',
  'gender.3.d': 'var(--gray-2)',
  'levels.2.kanton': 'blue',
  'levels.1.bund': 'red',
  'levels.3.gemeinde': 'green',
  'levels.3.gemeindeklein': 'yellow',
  participated: 'var(--petrol-dark-2)',
}

const LABELS = {
  gemeinde_gross: 'Gemeinde (gross)',
  gemeinde_mittel: 'Gemeinde (mittel)',
  gemeinde_klein: 'Gemeinde (klein)',
  bund: 'Bund',
  'levels.1.bund': (
    <div>
      <b>National</b>
    </div>
  ),
  'levels.2.kanton': (
    <div>
      <b>Kantonal</b>
    </div>
  ),
  'levels.3.gemeinde': (
    <div>
      <b>Grössere Gemeinden</b>
    </div>
  ),
  'levels.3.gemeindeklein': (
    <div>
      <b>Kleinere Gemeinden</b>
    </div>
  ),
  assault: 'Melden Angriffe',
  noassault: 'Keine Angriffe',
  participated: '', // 'Teilnehmer und Teilnehmerinnen',
  m: 'Männlich',
  f: 'Weiblich',
  'offenderType.1.parlamentarier': (
    <div>
      <b>Parlamen&shy;tarierinnen</b>
    </div>
  ),
  'offenderType.2.buerger': (
    <div>
      <b>Bürger</b>
    </div>
  ),
  'offenderType.3.sonstiges': (
    <div>
      <b>Andere</b>
    </div>
  ),
  'offenderType.4.na': <div>Kein Angriff</div>,
  'offenderType.5.unbekannt': 'Unbekannt',
  'gender.1.m': (
    <div>
      <b>Männer</b>
    </div>
  ),
  'offenderGender.1.m': (
    <div>
      <b>Männer</b>
    </div>
  ),
  'gender.2.f': (
    <div>
      <b>Frauen</b>
    </div>
  ),
  'offenderGender.2.f': '<div><b>Frauen</b></div>',
  'gender.3.d': (
    <div>
      <b>Divers</b>
    </div>
  ),
  'offenderGender.3.na': 'Kein Angriff',
  'injuryWho.1.m': (
    <div>
      <b>Männer</b>
    </div>
  ),
  'injuryWho.2.f': (
    <div>
      <b>Frauen</b>
    </div>
  ),
  'injuryWho.3.na': <div>kein Angriff</div>,
  'injuryWho.4.unbekannt': 'Unbekannt',
  'injuryCause.2.haltung': (
    <div>
      <b>pol. Haltung</b>
    </div>
  ),
  'injuryCause.3.gender': (
    <div>
      <b>Geschlecht</b>
    </div>
  ),
  'injuryCause.5.alter': (
    <div>
      <b>Alter</b>
    </div>
  ),
  'injuryCause.7.sonstiges': (
    <div>
      <b>anderen Gründen</b>
    </div>
  ),
  'injuryCause.8.na': <div>Kein Angriff</div>,
}
export const DotVizD3 = ({
  data,
  groupBy,
  coloredBy,
  markedBy,
  highlighted,
  showGroupLabels = false,
  colorMode = 'light',
  title,
  name,
  nameDirection = 'right',
}: {
  data: { [key: string]: string | number }[]
  groupBy: string
  coloredBy: string
  markedBy?: string
  highlighted?: number
  showGroupLabels?: boolean
  colorMode?: 'light' | 'dark' | undefined
  title?: string
  name?: string
  nameDirection?: 'left' | 'right'
}) => {
  const { width, height } = useWindowSize()
  const isMobile = width < 712
  const [windowHeight, setWindowHeight] = useState(0)
  const groupSpace = width < 712 ? 23 : 70
  const cols = 49
  const cellsize = Math.min(8, Math.round((width - 40) / cols)) // width < 712 ? 6 : 9

  const svgRef = useRef<SVGSVGElement>(null)
  const svgSize = { width: isMobile ? width : 668, height: windowHeight * 0.75 }
  const ref = useRef<HTMLDivElement | null>(null)
  const debouncedValue = useDebounce<number>(width, 250)

  const circleSize = width < 712 ? 2 : 3
  const rows = Math.round(data.length / cols) + 8
  let markerPosition: { x: number; y: number; color: string } | undefined =
    undefined

  const [positionGrid, setPositionGrid] = useState(
    generateGrid(cols, rows, cellsize, 0, 100)
  )
  const groupedData: [string, { [key: string]: string | number }[]][] =
    groupDataBy(data, (item: any) => getKeyValue(item, groupBy)).sort((a, b) =>
      (a[0] + '').localeCompare(b[0] + '')
    )
  const groupMeta: GroupMeta[] = getGroupMeta(
    groupedData,
    rows,
    positionGrid,
    coloredBy
  )
  const highlightElement = data.find((d) => d.id === highlighted)
  const highlightGroup = highlightElement
    ? highlightElement[groupBy]
    : undefined
  const neededCols = groupMeta
    .map((g) => g.neededCols)
    .reduce((a, b) => a + b, 0)

  const groupCount = groupMeta.length
  let fullWidth = neededCols * cellsize + (groupCount - 1) * groupSpace
  let fullHeight = rows * cellsize + (groupCount - 1) * groupSpace

  if (width < 712) {
    fullWidth = width
    fullHeight = rows * cellsize + (groupCount - 1) * groupSpace
  }
  const metaClone = JSON.parse(JSON.stringify(groupMeta)) as GroupMeta[]

  const drawData = {}
  data.forEach((d) => {
    const group = metaClone.find((g) => g.name === d[groupBy])
    if (!group) return null

    const posIndex = group.positions.findIndex((p) => p.color === d[coloredBy])
    const pos = group.positions[posIndex]
    group.positions.splice(posIndex, 1)

    if (!pos) return null

    const isMarked =
      !markedBy || (markedBy && Object.values(d).includes(markedBy))

    const isHighlighted = highlighted === d.id

    // Switch x and y on mobile for vertical view
    let x = pos.x + group.rank * groupSpace
    let y = pos.y
    if (width < 712) {
      x = pos.y + (svgSize.width - group.lastX) / 2
      y = pos.x + group.rank * groupSpace
    }

    if (isHighlighted) {
      markerPosition = {
        x: x,
        y: y,
        color: COLORS[d[coloredBy]],
      }
    }

    drawData[d.id] = {
      id: d.id,
      pos: { x, y },
      fill: isMarked
        ? isHighlighted
          ? COLORS[d[coloredBy]]
          : highlighted
          ? colorMode === 'dark'
            ? DARKCOLORS[d[coloredBy]]
            : BRIGHTCOLORS[d[coloredBy]]
          : COLORS[d[coloredBy]]
        : 'none',
      stroke: isHighlighted
        ? COLORS[d[coloredBy]]
        : highlighted
        ? colorMode === 'dark'
          ? DARKCOLORS[d[coloredBy]]
          : BRIGHTCOLORS[d[coloredBy]]
        : colorMode === 'dark'
        ? DARKCOLORS[d[coloredBy]]
        : BRIGHTCOLORS[d[coloredBy]],
      r: circleSize,
    }
  })

  useEffect(() => {
    setWindowHeight(height)
  }, [])

  useEffect(() => {
    if (!svgRef.current) return
    let svg = d3.select(svgRef.current)
    svg
      .select('.viz')
      .selectAll('circle')
      .data(data)
      .join(
        (enter) =>
          enter
            .append('circle')
            .attr('r', circleSize)
            .style('stroke', 'var(--site-background)')
            .style('fill', 'var(--site-background)')
            .attr('cx', (d) => drawData[d.id].pos.x)
            .attr('cy', (d) => drawData[d.id].pos.y),

        (update) =>
          update.call((update) =>
            update
              .attr('r', circleSize)
              .style('stroke', (d) => drawData[d.id].stroke)
              .style('fill', (d) => drawData[d.id].fill)
              .transition()
              .duration(500)
              .ease(bounce(0.02))

              .attr('cx', (d) => drawData[d.id].pos.x)
              .attr('cy', (d) => drawData[d.id].pos.y)
          ),
        (exit) => exit.remove()
      )
  }, [
    groupBy,
    coloredBy,
    markedBy,
    highlighted,
    svgRef.current,
    debouncedValue,
    windowHeight,
    colorMode,
  ])

  useEffect(() => {
    setWindowHeight(height)
  }, [width])

  useEffect(() => {
    setPositionGrid(generateGrid(cols, rows, cellsize, 0, 0))
  }, [rows, svgRef.current])

  const topMargin = height * 0.05
  return (
    <DotVizRoot ref={ref}>
      <SVG
        ref={svgRef}
        style={{ overflow: 'visible' }}
        width={width + 'px'}
        height={svgSize.height + 'px'}
        viewBox={'0 0 ' + fullWidth + ' ' + svgSize.height}
      >
        <g className='viz' transform={`translate(${0},${topMargin})`}>
          <Charttitle
            x={
              isMobile
                ? groupMeta[0].startPos.y +
                  (svgSize.width - groupMeta[0].lastX) / 2 -
                  circleSize
                : -circleSize
            }
            textAnchor='start'
            y={
              isMobile
                ? groupMeta[0].startPos.x - groupSpace * 1.25
                : -groupSpace
            }
          >
            {title}
          </Charttitle>
          {showGroupLabels && (
            <g>
              {groupMeta.map((group, i) => {
                const { startPos } = group
                let x = startPos.x + i * groupSpace - circleSize
                let y = startPos.y - groupSpace * 0.75
                let width = 100
                let height = 100
                if (window.innerWidth < 712) {
                  y = startPos.x + i * groupSpace - groupSpace + circleSize
                  x =
                    startPos.y + (svgSize.width - group.lastX) / 2 - circleSize
                  width = 300
                }
                return (
                  <g transform={`translate(${x}, ${y})`}>
                    <ForeignObject
                      width={width}
                      height={height}
                      opacity={
                        highlighted && group.name !== highlightGroup ? 0.2 : 1
                      }
                    >
                      <div>{LABELS[group.name]}</div>
                    </ForeignObject>
                  </g>
                )
              })}
            </g>
          )}
        </g>

        <g
          transform={`translate(${
            markerPosition ? markerPosition.x : svgSize.width / 2
          },${
            markerPosition ? markerPosition.y + topMargin : svgSize.height / 2
          })`}
        >
          {markerPosition && highlighted && name && (
            <>
              <NameTag
                color={
                  (markerPosition && markerPosition.color) || 'transparent'
                }
                name={name}
                direction={nameDirection}
              />
            </>
          )}
          <Dot
            id={9999}
            key={'position_cell_ring'}
            pos={{
              x: 0,
              y: 0,
            }}
            r={markerPosition ? 20 : 0}
            fill={'transparent'}
            stroke={markerPosition ? markerPosition.color : 'transparent'}
            strokeWidth={4}
            hide={!highlighted}
            style={{ transition: 'all .5s cubic-bezier(0, 1, 0.32, 1.05)' }}
          />
        </g>
      </SVG>
    </DotVizRoot>
  )
}

const NameTag = ({
  color = 'transparent',
  name,
  direction = 'right',
  hide = false,
}: {
  color: string
  name: string
  direction: 'left' | 'right'
  hide?: boolean
}) => {
  if (direction === 'left') {
    return (
      <g>
        <rect x={-150} y={6} fill={color} width={140} height={30} />
        <NameLabel
          transform={`translate(${-75},${26})`}
          textAnchor='middle'
          fill={color === 'transparent' ? 'rgba(0,0,0,0)' : undefined}
        >
          {name}
        </NameLabel>
      </g>
    )
  }
  return (
    <g style={{ display: hide ? 'none' : undefined }}>
      <rect x={10} y={6} fill={color} width={140} height={30} />
      <NameLabel
        textAnchor='middle'
        transform={`translate(${85},${26})`}
        fill={color === 'transparent' ? 'rgba(0,0,0,0)' : undefined}
      >
        {name}
      </NameLabel>
    </g>
  )
}
const SVG = styled.svg`
  circle {
    /* transition: fill 3.5s ease; */
  }
`

const Charttitle = styled.text`
  font-size: 14px;
  letter-spacing: 0.4px;
  fill: var(--text-color);
  @media screen and (max-width: 712px) {
    font-size: 13px;
  }
`

const NameLabel = styled.text`
  font-size: 15px;
  fill: white;
`
const DotVizRoot = styled.div``
const ForeignObject = styled.foreignObject`
  line-height: 1.4em;
  font-size: 15px;

  @media (max-width: 712px) {
    font-size: 13px;
  }
`
const Dot = ({
  id,
  pos,
  r = 1,
  fill = 'transparent',
  stroke = 'transparent',
  strokeWidth = 1,
  opacity = 1,
  style,
  hide = false,
}: {
  id?: number
  pos: { x: number; y: number }
  r?: number
  fill?: string
  stroke?: string
  strokeWidth?: number
  opacity?: number
  style?: any
  hide?: boolean
}) => {
  return (
    <>
      <Circle
        className={'animated_circle ' + id}
        cx={pos.x}
        cy={pos.y}
        r={r}
        fill={fill}
        stroke={stroke}
        // opacity={opacity}
        strokeWidth={strokeWidth}
        style={{ ...style, opacity: hide ? 0 : undefined }}
        // style={{ transition: 'all 0.5s cubic-bezier(0.4, -0.2, 0.1, 1.3)' }}
        onClick={() => {
          console.log(id)
        }}
      />
    </>
  )
}
const Circle = styled.circle`
  /* transition: cx 0.5s cubic-bezier(0.4, -0.2, 0.1, 1.3) !important; */
`
//cx 0.5s cubic-bezier(0.4, -0.2, 0.1, 1.3)
function groupDataBy(list: any[], keyGetter: (item: any) => string) {
  const map = new Map()
  list.forEach((item) => {
    const key = keyGetter(item)

    const collection = map.get(key)
    if (!collection) {
      map.set(key, [item])
    } else {
      collection.push(item)
    }
  })

  return Array.from(map)
}

const getKeyValue = (obj: any, key: string) => {
  return obj[key]
}

function getGroupMeta(
  groupedData: any[],
  rows: number,
  positionGrid: any[],
  coloredBy: string
) {
  var meta: GroupMeta[] = []
  groupedData.forEach((group, i) => {
    const neededCols = Math.round(group[1].length / rows)

    // get columns for group from grid
    var colrange = [
      meta.length === 0 ? 0 : meta[+(meta.length - 1)].colrange[1] + 1,
      meta.length === 0
        ? neededCols
        : meta[+(meta.length - 1)].colrange[1] + 1 + neededCols,
    ]

    // get positions for group from grid positions
    let positions = getPositionsForGroup(positionGrid, colrange)
    let firstPosition = positions[0]
    let maxX = Math.max(...positions.map((p) => p.y))
    // group data for grouep by color proptery
    const groupedGroup: {
      id: string
      count: number
    }[] = groupDataBy(group[1], (item: any) => getKeyValue(item, coloredBy))
      .sort((a, b) => (b[1].length - a[1].length ? 1 : -1))
      .map((m) => ({ id: m[0], count: m[1].length }))
      .sort((a, b) => b.count - a.count)

    // add mark for coloring to group positions
    positions = markColorPositions(positions, groupedGroup)

    meta.push({
      name: group[0],
      count: group[1].length,
      neededCols,
      colrange,
      positions,
      rank: i,
      startPos: firstPosition,
      lastX: maxX,
    })
  })
  return meta
}

function generateGrid(
  cols: number,
  rows: number,
  cellsize: number,
  minx = 0,
  miny = 0
) {
  const grid = []

  for (let i = 0; i < cols; i++) {
    for (let j = 0; j < rows; j++) {
      grid.push({
        x: Math.round(minx + i * cellsize),
        y: Math.round(miny + j * cellsize),
        col: i,
        row: j,
      })
    }
  }

  return grid
}
function getPositionsForGroup(positionGrid: DotPosition[], colrange: number[]) {
  let positions = selectCols(positionGrid, colrange)
  return positions
}

function markColorPositions(
  positions: DotPosition[],
  markingArr: {
    id: string
    count: number
  }[]
) {
  var pos_marked: DotPosition[] = []
  markingArr.forEach((mark, i) => {
    var pos_for_group = positions
      .sort((a, b) => {
        return a.col - b.col || a.row - b.row
      })
      .splice(0, mark.count)
    pos_for_group = pos_for_group.map((m) => {
      m.color = mark.id
      return m
    })
    pos_marked.push(...pos_for_group)
  })
  return pos_marked
}

function selectCols(data: DotPosition[], [from, to]: number[]) {
  return data.filter((d) => d.col >= from && d.col <= to)
}

export type GroupMeta = {
  name: string
  count: number
  neededCols: number
  colrange: number[]
  positions: DotPosition[]
  colorGroups?: GroupMeta[]
  rank: number
  startPos: DotPosition
  lastX: number
}
export type DotPosition = {
  x: number
  y: number
  col: number
  row: number
  color?: string
}

function bounce(h: number) {
  if (!arguments.length) h = 0.25
  var b0 = 1 - h,
    b1 = b0 * (1 - b0) + b0,
    b2 = b0 * (1 - b1) + b1,
    x0 = 2 * Math.sqrt(h),
    x1 = x0 * Math.sqrt(h),
    x2 = x1 * Math.sqrt(h),
    t0 = 1 / (1 + x0 + x1 + x2),
    t1 = t0 + t0 * x0,
    t2 = t1 + t0 * x1,
    m0 = t0 + (t0 * x0) / 2,
    m1 = t1 + (t0 * x1) / 2,
    m2 = t2 + (t0 * x2) / 2,
    a = 1 / (t0 * t0)
  return function (t) {
    return t >= 1
      ? 1
      : t < t0
      ? a * t * t
      : t < t1
      ? a * (t -= m0) * t + b0
      : t < t2
      ? a * (t -= m1) * t + b1
      : a * (t -= m2) * t + b2
  }
}
